import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock, withCtx as _withCtx, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "flex flex-col flex-1 min-h-0 pt-4" };
const _hoisted_2 = { class: "flex flex-col h-full min-h-0" };
const _hoisted_3 = { class: "flex items-center justify-between gap-2 px-5 h-14 shrink-0 border-b border-gainsboro dark:border-(--colors-border-base)" };
const _hoisted_4 = { class: "flex items-center gap-2" };
const _hoisted_5 = { class: "text-base font-semibold text-(--colors-text-default)" };
const _hoisted_6 = { class: "flex-1 overflow-x-hidden min-h-0" };
const _hoisted_7 = {
    key: 0,
    class: "h-full flex flex-col"
};
import { Avatar, Button, DataTable, Pager, TableShell, formatPhoneForDisplay, useTableQuery, useTableViews, } from "@eudonet/opal";
import { computed, h, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";
import { useOrganisationContacts } from "@/modules/contact/composables/useOrganisationContacts";
import { usePermissions } from "@/shared/composables";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { useListSettings } from "@/shared/composables/useListSettings";
import { useListColumns } from "@/shared/composables/useListColumns";
import ModuleState from "@/shared/components/ModuleState.vue";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import CreateContactModal from "@/modules/contact/presentation/components/CreateContactModal.vue";
import CreateOrganisationRelationModal from "./CreateOrganisationRelationModal.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'OrganisationContactsTab',
    props: {
        organisationId: {},
        organisationName: {}
    },
    setup(__props) {
        const props = __props;
        const { t } = useI18n();
        const { canEditOphub: canEdit, disabledTooltip } = usePermissions();
        const showCreateModal = ref(false);
        const showCreateRelationModal = ref(false);
        const ROW_OPTIONS = [10, 30, 50];
        // ── Table query & views ───────────────────────────────────────────────────────
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0] });
        const tableViews = useTableViews({ initialViews: [] });
        // ── Preset local views (Active / All) ─────────────────────────────────────────
        // These are local tabs — no BFF saved-filters for this contextual list.
        // Ids are matched in useOrganisationContacts to drive the includeEnded param.
        const localPresetViews = computed(() => [
            { id: 'org-contacts-active', label: t('contact.filters.active'), isPreset: true, isDefault: true, querySnapshot: {} },
            { id: 'org-contacts-all', label: t('contact.filters.all'), isPreset: true, isDefault: false, querySnapshot: {} },
        ]);
        const registeredViewIds = new Set();
        watch(localPresetViews, (views) => {
            const noPreviousViews = registeredViewIds.size === 0;
            views.forEach((v) => {
                if (registeredViewIds.has(v.id)) {
                    tableViews.updateView(v.id, { label: v.label });
                }
                else {
                    tableViews.addView(v);
                    registeredViewIds.add(v.id);
                }
            });
            if (noPreviousViews) {
                const defaultView = views.find((v) => v.isDefault);
                if (defaultView)
                    tableQuery.setView(defaultView.id);
            }
        }, { immediate: true });
        // ── Column settings ───────────────────────────────────────────────────────────
        const columnSettings = useListSettings({
            entity: 'organisation-contacts',
            columns: () => [
                { key: 'fullName', label: t('contact.columns.fullName'), visible: true, pinned: false, disabled: true },
                { key: 'roleFunction', label: t('contact.columns.function'), visible: true, pinned: false },
                { key: 'startDate', label: t('contact.columns.startDate'), visible: true, pinned: false },
                { key: 'status', label: t('contact.columns.status'), visible: false, pinned: false },
                { key: 'birthDate', label: t('contact.columns.birthDate'), visible: false, pinned: false },
                { key: 'roleTitle', label: t('contact.columns.roleTitle'), visible: false, pinned: false },
                { key: 'endDate', label: t('contact.columns.endDate'), visible: false, pinned: false },
                { key: 'salutation', label: t('contact.columns.salutation'), visible: false, pinned: false },
                { key: 'externalId', label: t('contact.columns.externalId'), visible: false, pinned: false },
                { key: 'createdAt', label: t('contact.columns.createdAt'), visible: false, pinned: false },
                { key: 'updatedAt', label: t('contact.columns.updatedAt'), visible: false, pinned: false },
                { key: 'courtesy', label: t('contact.columns.courtesy'), visible: false, pinned: false },
                { key: 'maidenName', label: t('contact.columns.maidenName'), visible: false, pinned: false },
                { key: 'gender', label: t('contact.columns.gender'), visible: false, pinned: false },
                { key: 'countryOfResidence', label: t('contact.columns.countryOfResidence'), visible: false, pinned: false },
                { key: 'preferredLanguage', label: t('contact.columns.preferredLanguage'), visible: false, pinned: false },
                { key: 'email', label: t('contact.columns.email'), visible: false, pinned: false },
                { key: 'telephone', label: t('contact.columns.telephone'), visible: false, pinned: false },
            ],
        });
        const columnOrder = computed(() => columnSettings.order.value);
        const columnPinning = computed(() => ({
            left: columnSettings.pinning.value.left ?? [],
            right: columnSettings.pinning.value.right ?? [],
        }));
        // ── Data loading ──────────────────────────────────────────────────────────────
        const { contacts, pageResponse, loading, loadError, reload } = useOrganisationContacts(() => props.organisationId, tableQuery);
        // Re-fetch when the organisation changes via pager navigation (component instance is reused).
        // Reset to page 1 first if not already there; setPage(1) triggers useSearch to re-fetch.
        watch(() => props.organisationId, () => {
            if (tableQuery.state.page === 1)
                reload();
            else
                tableQuery.setPage(1);
        });
        // ── List column helpers ───────────────────────────────────────────────────────
        const { formatDate, tOrRaw, formatRegion, formatLanguage, optionalColumn } = useListColumns({
            headerNamespace: 'contact.columns',
            columnSettings,
            minWidth: 130,
        });
        // ── TableShell labels ─────────────────────────────────────────────────────────
        const tableLabels = computed(() => buildTableLabels(t));
        // ── Columns ───────────────────────────────────────────────────────────────────
        const columns = [
            {
                id: "fullName",
                header: t("contact.columns.fullName"),
                minWidth: 200,
                cell: ({ row }) => {
                    const { firstName, lastName, contactId } = row.original;
                    return h("div", { class: "flex items-center gap-2" }, [
                        h(Avatar, { name: `${firstName} ${lastName}`, presentational: true }),
                        h(RouterLink, {
                            to: { name: "ContactDetail", params: { id: contactId } },
                            class: "text-blue-600 hover:underline",
                            onClick: (e) => e.stopPropagation(),
                        }, () => `${firstName} ${lastName}`),
                    ]);
                },
                draggable: false,
            },
            {
                id: "status",
                header: t("contact.columns.status"),
                minWidth: 120,
                cell: ({ row }) => row.original.status
                    ? t(`contact.status.${row.original.status}`, row.original.status)
                    : "–",
                draggable: true,
            },
            {
                id: "birthDate",
                header: t("contact.columns.birthDate"),
                minWidth: 130,
                cell: ({ row }) => formatDate(row.original.birthDate),
                draggable: true,
            },
            {
                id: "roleTitle",
                header: t("contact.columns.roleTitle"),
                minWidth: 140,
                cell: ({ row }) => row.original.roleTitle ?? "–",
                draggable: true,
            },
            {
                id: "roleFunction",
                header: t("contact.columns.function"),
                minWidth: 140,
                cell: ({ row }) => row.original.roleFunction
                    ? t(`contact.function.${row.original.roleFunction}`, row.original.roleFunction)
                    : "–",
                draggable: true,
            },
            {
                id: "startDate",
                header: t("contact.columns.startDate"),
                minWidth: 130,
                cell: ({ row }) => formatDate(row.original.startDate),
                draggable: true,
            },
            {
                id: "endDate",
                header: t("contact.columns.endDate"),
                minWidth: 130,
                cell: ({ row }) => formatDate(row.original.endDate),
                draggable: true,
            },
            optionalColumn('salutation', ({ row }) => row.original.salutation
                ? tOrRaw(`contact.create.fields.salutation.options.${row.original.salutation.toLowerCase()}`, row.original.salutation)
                : '–'),
            optionalColumn('externalId', ({ row }) => row.original.externalId || '–'),
            optionalColumn('createdAt', ({ row }) => formatDate(row.original.createdAt)),
            optionalColumn('updatedAt', ({ row }) => formatDate(row.original.updatedAt)),
            optionalColumn('courtesy', ({ row }) => row.original.courtesy || '–'),
            optionalColumn('maidenName', ({ row }) => row.original.maidenName || '–'),
            optionalColumn('gender', ({ row }) => {
                const value = row.original.gender;
                if (!value)
                    return '–';
                const key = value.charAt(0).toLowerCase() + value.slice(1);
                return tOrRaw(`contact.detail.options.gender.${key}`, value);
            }),
            optionalColumn('countryOfResidence', ({ row }) => formatRegion(row.original.countryOfResidence)),
            optionalColumn('preferredLanguage', ({ row }) => formatLanguage(row.original.preferredLanguage)),
            optionalColumn('email', ({ row }) => row.original.primaryEmail || '–'),
            optionalColumn('telephone', ({ row }) => row.original.primaryPhone ? formatPhoneForDisplay(row.original.primaryPhone) : '–'),
        ];
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createVNode(_unref(TableShell), {
                        query: _unref(tableQuery),
                        views: _unref(tableViews),
                        labels: tableLabels.value,
                        permissions: { canSaveView: false, canShareView: false },
                        "column-settings": _unref(columnSettings),
                        "show-view-selector": false,
                        "show-filter": false
                    }, {
                        table: _withCtx(() => [
                            _createElementVNode("div", _hoisted_2, [
                                _createElementVNode("div", _hoisted_3, [
                                    _createElementVNode("div", _hoisted_4, [
                                        _cache[4] || (_cache[4] = _createElementVNode("span", {
                                            class: "ri-contacts-line text-lg text-(--colors-text-secondary)",
                                            "aria-hidden": "true"
                                        }, null, -1 /* CACHED */)),
                                        _createElementVNode("h2", _hoisted_5, _toDisplayString(_ctx.$t('contact.title')), 1 /* TEXT */)
                                    ]),
                                    _createVNode(_unref(Button), {
                                        label: _ctx.$t('organisation.detail.contacts.createRelation'),
                                        "icon-left": "ri-link",
                                        "data-testid": "org-contacts-link-button",
                                        onClick: _cache[0] || (_cache[0] = ($event) => (showCreateRelationModal.value = true))
                                    }, null, 8 /* PROPS */, ["label"])
                                ]),
                                _createElementVNode("div", _hoisted_6, [
                                    (_unref(loading) || _unref(loadError) || _unref(contacts).length === 0)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                            (_unref(loading))
                                                ? (_openBlock(), _createBlock(ModuleLoading, { key: 0 }))
                                                : (_unref(loadError))
                                                    ? (_openBlock(), _createBlock(ModuleState, {
                                                        key: 1,
                                                        type: "error",
                                                        class: "flex-1",
                                                        title: _unref(t)('contact.loadError'),
                                                        "cta-icon": "ri-refresh-line",
                                                        "cta-label": _unref(t)('common.retry'),
                                                        onCta: _unref(reload)
                                                    }, null, 8 /* PROPS */, ["title", "cta-label", "onCta"]))
                                                    : (_openBlock(), _createBlock(ModuleState, {
                                                        key: 2,
                                                        type: "empty",
                                                        class: "flex-1",
                                                        title: _unref(t)('organisation.detail.contacts.empty.title'),
                                                        "cta-icon": "ri-link",
                                                        "cta-label": _unref(t)('organisation.detail.contacts.createRelation'),
                                                        "cta-disabled": !_unref(canEdit),
                                                        "cta-disabled-tooltip": _unref(disabledTooltip),
                                                        onCta: _cache[1] || (_cache[1] = ($event) => (showCreateRelationModal.value = true))
                                                    }, null, 8 /* PROPS */, ["title", "cta-label", "cta-disabled", "cta-disabled-tooltip"]))
                                        ]))
                                        : (_openBlock(), _createBlock(_unref(DataTable), {
                                            key: 1,
                                            columns: columns,
                                            data: _unref(contacts),
                                            "column-visibility": _unref(columnSettings).visibility,
                                            "column-order": columnOrder.value,
                                            "column-pinning": columnPinning.value,
                                            "column-sizing": _unref(columnSettings).widths.value,
                                            "has-settings": "",
                                            "sticky-header": "",
                                            onSettingsClick: _unref(columnSettings).openDrawer,
                                            "onUpdate:columnSizing": _unref(columnSettings).handleSizingChange
                                        }, null, 8 /* PROPS */, ["data", "column-visibility", "column-order", "column-pinning", "column-sizing", "onSettingsClick", "onUpdate:columnSizing"]))
                                ])
                            ])
                        ]),
                        pagination: _withCtx(() => [
                            _createVNode(_unref(Pager), {
                                "current-page": _unref(pageResponse)?.currentPage ?? _unref(tableQuery).state.page,
                                "total-pages": _unref(pageResponse)?.totalPages ?? 0,
                                "total-items": _unref(pageResponse)?.totalItems ?? 0,
                                "items-per-page": _unref(tableQuery).state.pageSize,
                                "rows-per-page-options": ROW_OPTIONS,
                                "rows-per-page-label": _ctx.$t('pagination.rowsPerPage'),
                                "items-count-separator": _ctx.$t('pagination.of'),
                                "previous-label": _ctx.$t('pagination.previous'),
                                "next-label": _ctx.$t('pagination.next'),
                                "show-go-to-page": "",
                                "go-to-page-label": _ctx.$t('pagination.goToPage'),
                                "go-label": _ctx.$t('pagination.go'),
                                onPageChange: _unref(tableQuery).setPage,
                                onItemsPerPageChange: _unref(tableQuery).setPageSize
                            }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["query", "views", "labels", "column-settings"])
                ]),
                _createVNode(CreateContactModal, {
                    modelValue: showCreateModal.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((showCreateModal).value = $event)),
                    "redirect-on-create": false,
                    "initial-organisation-id": __props.organisationId,
                    "initial-organisation-label": __props.organisationName ?? __props.organisationId,
                    onCreated: _unref(reload)
                }, null, 8 /* PROPS */, ["modelValue", "initial-organisation-id", "initial-organisation-label", "onCreated"]),
                _createVNode(CreateOrganisationRelationModal, {
                    modelValue: showCreateRelationModal.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((showCreateRelationModal).value = $event)),
                    "organisation-id": __props.organisationId,
                    "organisation-name": __props.organisationName ?? __props.organisationId,
                    onCreated: _unref(reload)
                }, null, 8 /* PROPS */, ["modelValue", "organisation-id", "organisation-name", "onCreated"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
